import type { Slide } from '@/framework/types';
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  ColumnsSlide,
} from '@/framework/layouts';
import {
  PriorArtTimeline,
  Kafka6020Timeline,
  DumbBrokerDiagram,
  CostDonut,
  EnvelopePile,
  ConveyorCompare,
  useSlideActivation,
  // "So we built one" section (slides 8-11):
  TrajectoryTimeline,
  TwoComponents,
  TrinityCards,
  DynamicFlow,
  // Architecture section (slides 15-19):
  MasterMapSequence,
  OutputTopicsSlide,
  ControlPlaneRing,
  ProcessorSlide,
  ScalingThree,
  // Demo · Stock Watchlist section (slides 17-19):
  ThreeLayersSlide,
  ScenarioSlide,
  WiringSlide,
  // KafkaDumbBrokerDiagram — parked for the later Kafka-vs-others slide
} from './diagrams';
import kafka6020JiraScreenshot from './images/kafka-6020-jira.png';
import snTemplateBg from './images/sn-template-bg.png';
import snTemplateConstellation from './images/sn-template-constellation.png';
import snTemplateWordmark from './images/sn-template-wordmark.png';
import speakerDavid from './images/davidk-k.jpeg';
import speakerAlvaro from './images/Alvaro.jpeg';
import snTemplateQaPattern from './images/sn-template-qa-pattern.png';
import snTemplateCtaBg from './images/sn-template-cta-bg.jpg';
import snTemplateSplitBg from './images/sn-template-split-bg.jpg';
import slide17StockBg from './images/sn-slide17-stockmarket.jpg';
import slide22LatencyBg from './images/sn-slide22-latency.svg';
import slide09ArchBg from './images/sn-slide09-architecture.svg';

/* ════════════════════════════════════════════════════════════════════════
   Berlin Buzzwords 2026 · Everything You Wanted from Broker-Side Filtering
   (and More): Solving KAFKA-6020
   ────────────────────────────────────────────────────────────────────────
   First draft. 40-minute demo-led talk; demo lands at minute 7. See
   Talk-Outline.md in the Berlin-Buzzwords-2026 working folder for the
   section-by-section time budget and rationale.

   Open dependencies flagged as TODO in speaker notes:
     - p99 latency number (slide 19) — needs `perf/` run against the
       current codebase.
     - architecture diagram (slide 10) — currently a SingleColumnSlide
       placeholder; replace with a FigureSlide once the diagram image is
       authored or a deck-local SVG component is added under ./diagrams.tsx.
     - parcels backup screenshot (slide 9) — same deal; placeholder text
       narrates what the screenshot will show.
   ════════════════════════════════════════════════════════════════════════ */

/* ──────────────────────────────────────────────────────────────────────
   Section 1 · Hook (3 min · slides 1–3)
   ──────────────────────────────────────────────────────────────────────
   Slide 1 — Title slide, styled per the StreamNative slide-template
   (template slide 4). Same assets as the demo divider (sn-template-bg +
   sn-template-wordmark) plus a date stripe above the headline. No
   constellation on this variant. NOTE: speaker bylines from the prior
   TitleSlide-based design are not currently rendered; if David wants
   them back, add a third text line below .sn-title-sub.
   ────────────────────────────────────────────────────────────────────── */

const SNTitleSlide = () => (
  <div className="slide-body sn-title-slide">
    <img src={snTemplateBg} alt="" className="dd-bg" aria-hidden="true" />
    <img src={snTemplateWordmark} alt="StreamNative" className="dd-brand" />
    <div className="dd-content sn-title-content">
      <p className="sn-title-date">08 June 2026</p>
      <h1 className="dd-headline">Everything You Wanted from Broker-Side Filtering</h1>
      <p className="dd-sub">Solving KAFKA-6020</p>
    </div>
    <div className="sn-title-speakers">
      <div className="sn-speaker">
        <div className="sn-speaker-avatar">
          <img src={speakerDavid} alt="David Kjerrumgaard" />
        </div>
        <div className="sn-speaker-text">
          <p className="sn-speaker-name">David Kjerrumgaard</p>
          <p className="sn-speaker-title">Principal Sales Engineer · StreamNative</p>
        </div>
      </div>
      <div className="sn-speaker">
        <div className="sn-speaker-avatar">
          <img src={speakerAlvaro} alt="Álvaro Rodríguez" />
        </div>
        <div className="sn-speaker-text">
          <p className="sn-speaker-name">Álvaro Rodríguez</p>
          <p className="sn-speaker-title">Solutions Engineer · StreamNative</p>
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Problem section · three new beats (slides 2 / 3 / 4)
   ──────────────────────────────────────────────────────────────────────
   Front-loaded per David's narrative restructure: hook the room with the
   problem, the cost impact, and the latency impact BEFORE the timeline
   and prior-art slides. Each beat is a one-headline + one-diagram slide
   following the framework's raw skeleton (.slide-body → .sn-header →
   .sn-work). Substance lives in Problem-Section-Three-Beats.md.
   ────────────────────────────────────────────────────────────────────── */

const ProblemBeatSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Every messaging team wants the same thing: <em>only the messages they care about</em>.
      </h2>
    </header>
    <div className="sn-work">
      <DumbBrokerDiagram />
    </div>
  </div>
);

const CostBeatSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        The dollars you waste are the bytes you <em>discard</em>.
      </h2>
      <p className="sn-sub">
        Picture a topic with <strong>500 unique keys</strong>, producers publishing at
        <strong> 35,000 msg/s</strong>. This consumer subscribes to <strong>3 of those keys</strong>.
      </p>
    </header>
    <div className="sn-work">
      <CostDonut />
    </div>
  </div>
);

const LatencyBeatSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Wasted bytes don&apos;t just cost dollars. They cost <em>wall-clock time</em>.
      </h2>
    </header>
    <div className="sn-work">
      <EnvelopePile />
    </div>
  </div>
);

const Slide2 = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">This is a <em>solved problem</em> — everywhere except Kafka.</h2>
    </header>
    <div className="sn-work">
      <PriorArtTimeline />
    </div>
  </div>
);

/* Comparison slide — bridges Prior Art (solved-elsewhere) to the
   KAFKA-6020 timeline (8 years of inaction). Shows BOTH outcomes from a
   shared firehose: prior-art broker filters at the source, Kafka forwards
   everything to a client that drowns. */
const ComparisonSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">Same firehose. <em>Two outcomes.</em></h2>
    </header>
    <div className="sn-work">
      <ConveyorCompare />
    </div>
  </div>
);

/* Inner content: timeline on top, JIRA screenshot overlay slides up from
   the bottom after a 4 s delay. Keyed by useSlideActivation so the
   slide-up replays each time the slide is re-entered. */
const Slide3Body = () => (
  <>
    <Kafka6020Timeline />
    <img
      className="kafka6020-jira-overlay"
      src={kafka6020JiraScreenshot}
      alt="KAFKA-6020 JIRA ticket — Status: OPEN, Resolution: Unresolved, Assignee: Unassigned, Created 06/Oct/17, last Updated 06/Nov/23, Labels: needs-kip."
    />
  </>
);

const Slide3 = () => {
  const [ref, activation] = useSlideActivation();
  return (
    <div className="slide-body" ref={ref}>
      <header className="sn-header">
        <h2 className="sn-h2">The community asked. <em>Nobody even drafted the proposal.</em></h2>
      </header>
      <div className="sn-work kafka6020-stage">
        <Slide3Body key={activation} />
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────
   Section 1b · "So we built one" (slides 8–11) — 4-slide layout
   ──────────────────────────────────────────────────────────────────────
   Matches the exploration's app.jsx ordering exactly: Trajectory opens
   the section (where this is going), then the two-component architecture
   reveal, then the SDK code (Consumer creation), then dynamic filter
   changes. Replaces the old single Setup slide AND absorbs the SDK code
   slide (was 16) + the dynamic-filter slide (was 17) + the trajectory
   slide (was 25). Each diagram wraps itself with useSlideActivation, so
   the slide shells stay lean.
   ────────────────────────────────────────────────────────────────────── */

const TrajectoryOpenerSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">So we built one — <em>our journey from Pulsar feature to Apache Kafka KIP.</em></h2>
    </header>
    <div className="sn-work">
      <TrajectoryTimeline />
    </div>
  </div>
);

const TwoComponentsSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">Two key components.</h2>
    </header>
    <div className="sn-work">
      <TwoComponents />
    </div>
  </div>
);

const ConsumerCreationSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">Native Kafka SDK — <em>Consumer Creation.</em></h2>
    </header>
    <div className="sn-work">
      <TrinityCards />
    </div>
  </div>
);

const DynamicFilterChangesSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">Native Kafka SDK — <em>Dynamic Filter Changes.</em></h2>
    </header>
    <div className="sn-work">
      <DynamicFlow />
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Section 1c · Demo divider (slide 12)
   ──────────────────────────────────────────────────────────────────────
   Section break between the SDK section (slides 8-11) and the architecture
   trinity (slide 13). Uses three real assets extracted from the
   StreamNative Google Slide Template PPTX (template slide 5):
     - sn-template-bg.png             — full wavy navy backdrop
     - sn-template-constellation.png  — composite S logomark + dot/square pattern
     - sn-template-wordmark.png       — StreamNative wordmark + logomark
   All styling lives in styles.css under .demo-divider.
   ────────────────────────────────────────────────────────────────────── */

const DemoDividerSlide = () => (
  <div className="slide-body demo-divider">
    <img src={snTemplateBg} alt="" className="dd-bg" aria-hidden="true" />
    <img src={snTemplateWordmark} alt="StreamNative" className="dd-brand" />
    <div className="dd-content">
      <h1 className="dd-headline">Broker-Side Filtering Demo</h1>
      <p className="dd-sub">Native Kafka Producer and Consumer</p>
    </div>
    <img src={snTemplateConstellation} alt="" className="dd-decoration" aria-hidden="true" />
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Section 2 · (formerly held SectionDivider, quote, three-taxes, and the
   concrete-trader slides — all dropped. Problem-section beats now live at
   slides 2/3/4; the hook flow goes straight from Setup into the Demo.)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 3 · (formerly LIVE DEMO — divider + parcels backup slides
   were dropped. The talk now goes directly from the SDK section into
   Architecture. If you bring the live demo back, the title card +
   backup slides go between current slides 11 and 12.)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 4 · (former "Three layers" trinity recap, Slide11, dropped in
   favor of the higher-fidelity ThreeLayersSlide that opens the demo
   section at id 17. SolutionDiagram + SingleColumnSlide imports are now
   gone with it — the new section uses ThreeLayersSlide / ScenarioSlide /
   WiringSlide entirely on the demo side.)
   ──────────────────────────────────────────────────────────────────────

   Section 5 · Architecture (slides 15–19) — 5-slide treatment
   ──────────────────────────────────────────────────────────────────────
   Replaces the prior 3-slide Two functions section (divider + prose-heavy
   selector-filter + orchestrator slides) with a diagram-forward five-slide
   architecture section. Reuses the master map's spatial law: control plane
   on top (indigo), data plane on bottom (teal); slides 16-19 are dimmed
   zoom-ins on the master map's regions. See `Architecture-Section-Brief.md`
   for the substance + framing posture (capability-first / choice-justified).
   ────────────────────────────────────────────────────────────────────── */

const ArchMasterSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Architecture at a glance — <em>two planes, one data flow.</em>
      </h2>
    </header>
    <div className="sn-work">
      <MasterMapSequence />
    </div>
  </div>
);

const ArchOutputTopicsSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Every filter gets its own output topic — <em>resilience by design.</em>
      </h2>
    </header>
    <div className="sn-work">
      <OutputTopicsSlide />
    </div>
  </div>
);

const ArchControlPlaneSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Control plane — <em>the Kubernetes-controller pattern.</em>
      </h2>
    </header>
    <div className="sn-work">
      <ControlPlaneRing />
    </div>
  </div>
);

const ArchProcessorSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Filter function — <em>one per source topic, deserialize once.</em>
      </h2>
    </header>
    <div className="sn-work">
      <ProcessorSlide />
    </div>
  </div>
);

const ArchScalingSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Scaling story + <em>trade-offs.</em>
      </h2>
    </header>
    <div className="sn-work">
      <ScalingThree />
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Section 6 · The Kafka SDK (entire section now lives in slides 8–13)
   ──────────────────────────────────────────────────────────────────────
   Everything from the original Kafka SDK section was absorbed into the
   new "So we built one" section: divider (was Slide15), SDK code (was
   Slide16), Dynamic by design (was Slide17), and the "Boring on purpose"
   design-decisions ColumnsSlide (was Slide18). The deck now flows
   directly from Two functions → Use case patterns.
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 6b · (formerly Use case patterns — slide dropped. The deck
   now goes directly from Architecture's Scaling + Trade-offs straight
   into Proof at Scale.)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 7 · Proof at Scale
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Demo intro section · section divider + 3 demo-intro slides
   ──────────────────────────────────────────────────────────────────────
   The divider clarifies the audience is about to see WHAT the live demo
   will show (three layers · scenario + cast · end-to-end wiring), not
   yet the demo itself. Visually it mirrors the Proof divider's layout
   (deep-navy bg + top-left section/subsection labels via the shared
   .sn-proof-divider classes).

   The three demo-intro slides wrap the diagram components from
   ./diagrams.tsx with .sn-h2 headings per the handoff README.
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Split section divider — SN template "Header" layout
   ──────────────────────────────────────────────────────────────────────
   Left ~52%: dark-navy panel with a two-line headline + subtitle.
   Right ~48%: full-bleed brand photo + centered constellation overlay.
   Used by the demo-intro divider (17) and the receipts divider (22).
   Assets from the SN template PPTX (template slide 5/6): the photo is the
   right half of image62; the constellation is image10 (same asset already
   in the deck as snTemplateConstellation). */
const SplitDivider = ({
  lines,
  sub,
  photo = snTemplateSplitBg,
  showConstellation = true,
  wideText = false,
}: {
  lines: readonly string[];
  sub: string;
  photo?: string;
  showConstellation?: boolean;
  wideText?: boolean;
}) => (
  <div className={`slide-body sn-split-divider${wideText ? ' sn-split-divider--wide' : ''}`}>
    <div className="ssd-left">
      <div className="ssd-stack">
        <h1 className="ssd-headline">
          {lines.map((l, i) => (
            <span key={i} className="ssd-line">{l}</span>
          ))}
        </h1>
        <p className="ssd-sub">{sub}</p>
      </div>
    </div>
    <div className="ssd-right">
      <img src={photo} alt="" className="ssd-photo" aria-hidden="true" />
      {showConstellation ? (
        <img src={snTemplateConstellation} alt="" className="ssd-constellation" aria-hidden="true" />
      ) : null}
    </div>
  </div>
);

/* Slide 17 photo: CC0 stock-market chart (rawpixel via Wikimedia Commons,
   public-domain dedication). Slide 22 keeps the default brand wave photo. */
const DemoIntroDividerSlide = () => (
  <SplitDivider
    lines={['What We’re', 'About to Demo']}
    sub="Three layers · Stock Watchlist · End-to-end wiring"
    photo={slide17StockBg}
    showConstellation={false}
  />
);

/* Solution-architecture section divider (after the trajectory slide).
   Right panel: original SN-blue tile stack — the three architecture layers
   (SDK · Control Plane · Data Plane) in blue, the three qualities
   (Performance · Resiliency · Scalability) in teal. */
const SolutionArchDividerSlide = () => (
  <SplitDivider
    lines={['Solution', 'Architecture']}
    sub="Overview"
    photo={slide09ArchBg}
    showConstellation={false}
  />
);

const DemoThreeLayersSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        Three layers. <em>Two of them are vanilla Kafka.</em>
      </h2>
    </header>
    <div className="sn-work">
      <ThreeLayersSlide />
    </div>
  </div>
);

const DemoScenarioSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        One stock stream, nine watchlists &mdash; <em>filtered at the broker.</em>
      </h2>
    </header>
    <div className="sn-work">
      <ScenarioSlide />
    </div>
  </div>
);

const DemoWiringSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">
        One producer, one filter, <em>nine private topics.</em>
      </h2>
    </header>
    <div className="sn-work">
      <WiringSlide />
    </div>
  </div>
);

/* Receipts section divider — reuses the SplitDivider template above.
   Right panel: original SN-blue "latency" graphic (transport → durability →
   filtering glass plaques + a latency gauge), authored for this deck. */
const Slide20 = () => (
  <SplitDivider
    lines={['Performance', 'at Scale']}
    sub="Latency & Cost"
    photo={slide22LatencyBg}
    showConstellation={false}
    wideText
  />
);

/* ──────────────────────────────────────────────────────────────────────
   Latency-proof trio · slides 23/24/25 — the honest decomposition arc
   ──────────────────────────────────────────────────────────────────────
   23 · TensionSlide       — the apparent contradiction (< 1 ms vs 125 ms)
   24 · DecompositionSlide — where the 125 ms goes (the money slide)
   25 · KeystoneSlide      — measured before/after (217 → 18 ms, co-located ~30 ms)

   Ported from the latency-proof handoff bundle. Numbers locked to the
   measured perf run: selector eval < 1 ms (clean broker clock), output
   persist ~15 ms, two persists ~25 ms (durability), inbound transport
   ~100 ms (subtraction), end-to-end p50 ~125 ms (9-query demo).

   Each slide uses useLatencyReveal — on mount (and on slide re-entry)
   it flips data-reveal from "0" to "1" after rAF + 80 ms, which drives
   the CSS entry transitions. Capture / print contexts that never run
   the JS keep the visible base state.
   ────────────────────────────────────────────────────────────────────── */

function useLatencyReveal() {
  const [ref, activation] = useSlideActivation();
  const [reveal, setReveal] = useState<'0' | '1'>('0');
  useEffect(() => {
    setReveal('0');
    const r1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setReveal('1'));
    });
    const t = window.setTimeout(() => setReveal('1'), 80);
    return () => {
      cancelAnimationFrame(r1);
      window.clearTimeout(t);
    };
  }, [activation]);
  return { ref, reveal };
}

const Slide21 = () => {
  const { ref, reveal } = useLatencyReveal();
  return (
    <div className="slide-body" ref={ref} data-reveal={reveal}>
      <header className="sn-header">
        <h2 className="sn-h2">One claim. One dashboard. <em>An apparent contradiction.</em></h2>
      </header>
      <div className="sn-work">
        <div className="lat-wrap">
          <div className="lat-tension">
            <div className="lat-tcol lat-tcol--claim">
              <span className="lat-k">On the slide &middot; the claim</span>
              <span className="lat-tnum lat-tnum--blue">&lt;&thinsp;1<sup>ms</sup></span>
              <span className="lat-tlabel">Sub-millisecond filtering</span>
              <span className="lat-tds">
                The selector evaluation itself &mdash; deserialize once, test every predicate, enqueue
                the matches. Timed on a clean broker clock.
              </span>
              <span className="lat-tag lat-tag--measured">
                <span className="dot" />Measured &middot; broker-only
              </span>
            </div>

            <div className="lat-tvs">
              <span className="lat-tvs-line" />
              <span className="lat-tvs-badge">both true</span>
              <span className="lat-tvs-line" />
            </div>

            <div className="lat-tcol lat-tcol--dash">
              <span className="lat-k">On the dashboard &middot; the demo</span>
              <span className="lat-tnum lat-tnum--outline">125<sup>ms</sup></span>
              <span className="lat-tlabel">p50 end-to-end &middot; 9-query demo</span>
              <span className="lat-tds">
                Producer timestamp &rarr; durably-persisted filtered copy. The{' '}
                <em style={{ fontStyle: 'normal', color: 'var(--sn-grey-8, #1A1F22)' }}>entire journey</em>
                {' '}&mdash; most of it happening before and around the filter, not inside it.
              </span>
              <span className="lat-tag lat-tag--inferred">
                <span className="dot" />End-to-end &middot; incl. transport
              </span>
            </div>
          </div>

          <p className="lat-tfoot">
            <strong>They measure different spans.</strong> The{' '}
            <span className="lat-mono">&lt;&thinsp;1&thinsp;ms</span> is the filter&rsquo;s CPU work; the{' '}
            <span className="lat-mono">125&thinsp;ms</span> is the whole pipeline &mdash; transport, durability,{' '}
            <em style={{ fontStyle: 'normal' }}>and</em> filtering.
          </p>
        </div>
      </div>
    </div>
  );
};

const LAT_BUCKETS: ReadonlyArray<{
  mod: 'transport' | 'persist' | 'filter';
  n: string;
  h: string;
  d: React.ReactNode;
}> = [
  {
    mod: 'transport',
    n: '~100 ms',
    h: 'Inbound transport',
    d: (
      <>
        A laptop talking to a GCP region over the <strong>public internet</strong>. Not the filter
        &mdash; not even the broker. A plain consumer with no filter pays the same road trip.
        Co-locate the producer and it collapses to <strong>~1 ms</strong>.
      </>
    ),
  },
  {
    mod: 'persist',
    n: '~25 ms',
    h: 'Durability · two persists',
    d: (
      <>
        Each message hits BookKeeper with a <strong>journal fsync</strong> on the way in, and the
        filtered copy is written on the way out. The price of <strong>not losing your data</strong>
        {' '}&mdash; paid by any durable pipeline.
      </>
    ),
  },
  {
    mod: 'filter',
    n: '< 1 ms',
    h: 'Selector evaluation',
    d: (
      <>
        The actual filtering. Deserialize once, evaluate all nine selectors, fan out the matches &mdash;
        the <strong>lowest latency cost</strong> in the whole profile.
      </>
    ),
  },
];

const Slide22 = () => {
  const { ref, reveal } = useLatencyReveal();
  return (
    <div className="slide-body" ref={ref} data-reveal={reveal}>
      <header className="sn-header">
        <h2 className="sn-h2">Where the 125 ms goes &mdash; <em>filtering is the thinnest slice.</em></h2>
      </header>
      <div className="sn-work">
        <div className="lat-decomp">
          <div className="lat-bar-frame">
            <div className="lat-bar">
              <div className="lat-seg lat-seg--transport" style={{ width: '80%' }}>
                <span className="lat-seg-v">~100 ms</span>
                <span className="lat-seg-k">Inbound transport &middot; public internet</span>
              </div>
              <div className="lat-seg lat-seg--persist" style={{ width: '19.2%' }}>
                <span className="lat-seg-v">~25 ms</span>
                <span className="lat-seg-k">Durability</span>
              </div>
              <div className="lat-seg lat-seg--filter" style={{ width: '0.8%' }} />
              <div className="lat-callout">
                <span className="lat-callout-v">&lt; 1 ms</span>
                <span className="lat-callout-k">Filtering</span>
                <span className="lat-callout-stem" />
              </div>
            </div>
            <div className="lat-axis">
              <span>0</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
              <span>125 ms</span>
            </div>
          </div>

          <div className="lat-buckets">
            {LAT_BUCKETS.map((b) => (
              <div key={b.mod} className={`lat-bucket lat-bucket--${b.mod}`}>
                <div className="lat-bucket-top">
                  <span className="lat-bucket-n">{b.n}</span>
                </div>
                <span className="lat-bucket-h">{b.h}</span>
                <span className="lat-bucket-d">{b.d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Slide23 = () => {
  const { ref, reveal } = useLatencyReveal();
  return (
    <div className="slide-body" ref={ref} data-reveal={reveal}>
      <header className="sn-header">
        <h2 className="sn-h2">We didn&rsquo;t model it &mdash; <em>we ran it.</em></h2>
      </header>
      <div className="sn-work">
        <div className="lat-key">
          <div className="lat-key-grid">
            {/* left · the measured before/after */}
            <div className="lat-cmp">
              <div className="lat-cmp-head">
                <span className="lat-cmp-title">Raw transport &middot; identical demo, same NARs, same 9 queries</span>
                <span className="lat-tag lat-tag--measured"><span className="dot" />Measured</span>
              </div>

              <div className="lat-row lat-row--cloud">
                <div className="lat-row-lab">
                  <b>Cloud</b>
                  <span>laptop &rarr; GCP &middot; 2 legs</span>
                </div>
                <div className="lat-track">
                  <div className="lat-fill lat-fill--cloud" style={{ width: '94%' }} />
                </div>
                <span className="lat-row-val">217 ms</span>
              </div>

              <div className="lat-row lat-row--local">
                <div className="lat-row-lab">
                  <b>Local</b>
                  <span>one machine &middot; 0 legs</span>
                </div>
                <div className="lat-track">
                  <div className="lat-fill lat-fill--local" style={{ width: '7.8%' }} />
                </div>
                <span className="lat-row-val">18 ms</span>
              </div>

              <div className="lat-delta">
                <span className="lat-delta-x">12&times;</span>
                <span className="lat-delta-tx">
                  Removing the public-internet path collapses transport <strong>12&times;</strong>. The{' '}
                  <code>199 ms</code> gap is two network legs &rarr; <strong>~100 ms each</strong>, proven by
                  subtraction &mdash; not estimated.
                </span>
              </div>
            </div>

            {/* right · the production payoff */}
            <div className="lat-payoff">
              <span className="lat-payoff-k">Co-locate, like production</span>
              <div className="lat-payoff-flow">
                <div className="lat-payoff-from">
                  <span className="lat-payoff-num">125 ms</span>
                  <span className="lat-payoff-sub">cloud demo</span>
                </div>
                <span className="lat-payoff-arrow">&rarr;</span>
                <div className="lat-payoff-to">
                  <span className="lat-payoff-num">~30 ms</span>
                  <span className="lat-payoff-sub">co-located</span>
                </div>
              </div>
              <p className="lat-payoff-line">
                Move the producer next to the broker and 125 becomes ~30.{' '}
                <strong>The filtering process itself is sub-millisecond</strong>; the rest is transport and
                durability &mdash; the dashboard shows the end-to-end latency, including transport.
              </p>
              <div className="lat-tagrow">
                <span className="lat-tag lat-tag--inferred"><span className="dot" />~30 ms inferred</span>
              </div>
            </div>
          </div>

          <div className="lat-foot">
            <span className="lat-foot-k">Why not Flink / KStreams?</span>
            <span className="lat-foot-b">
              Co-located, latency is <strong>comparable</strong> &mdash; a stream processor on the same
              substrate pays the same transport and the same two persists. The win is elsewhere:{' '}
              <strong>operational simplicity</strong> (no separate cluster to run) and{' '}
              <strong>built-in multi-query orchestration</strong> &mdash; deserialize once, evaluate N
              selectors, register and change them live.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────
   Cost receipt (slide 26) · trading compute for network
   ──────────────────────────────────────────────────────────────────────
   The dollar half of the Receipts section. One cheap filter instance buys
   a huge cut in cross-region egress on a live fintech workload — real,
   measurable, immediate, for a minimal latency cost. Numbers are the
   client's own figures (m6a.large @ $0.086/hr ≈ $62.78/mo; 95%+ traffic
   eliminated → 240 TB/mo ≈ $16,100/mo egress; ~256× return). */
const CostSlide = () => (
  <div className="slide-body">
    <header className="sn-header">
      <h2 className="sn-h2">Trading compute for network &mdash; <em>the savings are real, measurable, and immediate.</em></h2>
    </header>
    <div className="sn-work">
      <div className="cost-wrap">
        <div className="cost-grid">
          <div className="cost-card cost-card--spend">
            <span className="cost-card-k">What you spend · compute</span>
            <span className="cost-card-fig">$62.78<span className="cost-card-unit">/mo</span></span>
            <span className="cost-card-d">
              One filter function &mdash; <strong>2 cores / 8&thinsp;GB RAM</strong>, the compute
              equivalent of a single AWS <code>m6a.large</code> at <strong>$0.086/hr</strong>.
            </span>
          </div>

          <div className="cost-trade">
            <span className="cost-trade-x">&asymp;&thinsp;256&times;</span>
            <span className="cost-trade-l">return on spend</span>
          </div>

          <div className="cost-card cost-card--save">
            <span className="cost-card-k">What you save · network egress</span>
            <span className="cost-card-fig cost-fig--save">~$16,100<span className="cost-card-unit">/mo</span></span>
            <span className="cost-card-d">
              <strong>95%+</strong> of cross-region traffic eliminated &mdash;{' '}
              <strong>240&thinsp;TB/month</strong> that never leaves the broker.
            </span>
          </div>
        </div>

        <div className="cost-math">
          <div className="cost-math-row">
            <span className="cost-math-lab">Before</span>
            <span className="cost-math-eq">30 Mbps &times; 31 consumers = <b>930 Mbps</b></span>
            <span className="cost-math-note">all data to all consumers</span>
          </div>
          <div className="cost-math-row">
            <span className="cost-math-lab">After</span>
            <span className="cost-math-eq">5 Mbps &times; 31 = <b>155 Mbps</b></span>
            <span className="cost-math-note">filtered at the broker</span>
          </div>
          <div className="cost-math-row cost-math-row--net">
            <span className="cost-math-lab">Net</span>
            <span className="cost-math-eq">755 Mbps saved = <b className="cost-fig--save">240 TB / month</b></span>
            <span className="cost-math-note">even at small throughputs</span>
          </div>
        </div>

        <div className="cost-foot">
          <span className="cost-foot-k">Real · measurable · immediate</span>
          <span className="cost-foot-b">
            Day-one savings on a live fintech workload &mdash; bought for a{' '}
            <strong>minimal latency cost</strong> (~30&thinsp;ms co-located, sub-millisecond filtering).
            For a lot of developers, that&rsquo;s a trade well worth making.
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Section 7b · (formerly Trajectory — the TimelineSlide that lived here
   was dropped when the new "So we built one" section's TrajectorySlide
   absorbed all five milestones up front.)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 8 · (formerly Common Objections — divider + 4-card slide
   both dropped. The Q&A handles these directly without their own deck
   anchor. Also: scale-validation wrap-up dropped from the Proof section
   above — the stat trio + cost ratio carry the proof on their own.)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   Section 9 · Potential Enhancements
   Audience-engagement beat: name the obvious next axes for the selector
   language and invite the room to suggest more. The Q&A often opens
   here naturally.
   ────────────────────────────────────────────────────────────────────── */

const Slide28 = () => (
  <ColumnsSlide
    eyebrow="Roadmap · what would you build?"
    title={<>We shipped the foundation. <em>What&apos;s next?</em></>}
    variant="cards"
    cols={4}
    columns={[
      {
        num: '01',
        name: 'Content-based routing · DeSer + Schema Registry',
        desc:
          'Schema-aware predicates over the message body, e.g. price > 50000 AND volume > 100. The SDK reuses the consumer\'s already-configured key.deserializer / value.deserializer + Confluent Schema Registry to decode body fields — no new configuration, same minimally-invasive surface.',
      },
      {
        num: '02',
        name: 'Time-window selectors',
        desc:
          'Filters that fire only inside business-hour or trading-session windows, e.g. inWindow(\'NYSE_OPEN\', message.timestamp). Lets the same query express temporal predicates without an external scheduler.',
      },
      {
        num: '03',
        name: 'Remote enable / disable',
        desc:
          'Management-API toggle to pause or resume queries without a redeploy, e.g. an isActive flag on the registered selector. Useful for incident response and A/B experiments.',
      },
      {
        num: '04',
        name: 'Your turn',
        desc:
          'Bring us your real workloads. What\'s the next selector dimension we should ship? Let\'s Talk!',
      },
    ]}
  />
);

/* ──────────────────────────────────────────────────────────────────────
   Section 10 · Close (1 min · slides 26–27)
   ────────────────────────────────────────────────────────────────────── */

const REPO_URL = 'https://github.com/david-streamlio/broker-side-filtering-function';

/* ──────────────────────────────────────────────────────────────────────
   Run-Locally slide · styled per StreamNative template slide 92
   ──────────────────────────────────────────────────────────────────────
   Centered stack: small eyebrow → big heading → subtitle → QR → URL.
   Background is the SN template's dotted navy backdrop (image58.jpg).
   QR target + rendered URL is REPO_URL (broker-side-filtering-function).
   ────────────────────────────────────────────────────────────────────── */

const Slide29 = () => (
  <div className="slide-body sn-cta-slide">
    <img src={snTemplateCtaBg} alt="" className="sn-cta-bg" aria-hidden="true" />
    <div className="sn-cta-stack">
      <p className="sn-cta-eyebrow">Scan to Get the Code</p>
      <h1 className="sn-cta-heading">Run Locally</h1>
      <p className="sn-cta-sub">Scan to open the repo</p>
      <div className="sn-cta-qr">
        <QRCodeSVG value={REPO_URL} size={220} level="M" bgColor="#FFFFFF" fgColor="#0A1A36" />
      </div>
      <p className="sn-cta-url-line">
        <a href={REPO_URL} target="_blank" rel="noreferrer">
          {REPO_URL}
        </a>
      </p>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Closing slide (Q&A) · styled per StreamNative template slide 9
   ──────────────────────────────────────────────────────────────────────
   Same wavy navy backdrop as slide 1 and slide 12; right-anchored Q&A
   dot/square pattern; left column with eyebrow + body + two emails +
   streamnative.io link. No StreamNative wordmark on this layout (the
   template doesn't carry one on the closing slide).
   ────────────────────────────────────────────────────────────────────── */

const ClosingSlide = () => (
  <div className="slide-body sn-closing-slide">
    <img src={snTemplateBg} alt="" className="dd-bg" aria-hidden="true" />
    <img src={snTemplateQaPattern} alt="" className="sn-closing-pattern" aria-hidden="true" />
    <div className="dd-content sn-closing-content">
      <p className="sn-closing-eyebrow">THANK YOU FOR YOUR TIME</p>
      <p className="sn-closing-body">
        Let&rsquo;s make broker-side filtering part of Apache Kafka &mdash; together.
      </p>
      <p className="sn-closing-email">david@streamnative.io</p>
      <p className="sn-closing-email">alvaro.rodriguez@streamnative.io</p>
      <p className="sn-closing-link">
        <a href="https://www.streamnative.io" target="_blank" rel="noreferrer">
          www.streamnative.io
        </a>
      </p>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────
   Slide order + speaker notes
   ────────────────────────────────────────────────────────────────────── */

export const slides: readonly Slide[] = [
  {
    id: 1,
    theme: 'beige',
    section: 'Berlin Buzzwords 2026 · Berlin',
    notes: [
      `Key point: Kafka never shipped the one primitive every other messaging system has — filtering at the broker. We built it.`,
      `Script: I'm David, this is Álvaro. For the next 40 minutes we're talking about one thing Kafka has never been able to do: filter messages at the broker, so a consumer only receives what it actually asked for. The community requested it eight years ago — it's logged as KAFKA-6020 — and nobody ever shipped it. So we did, it's open source, and we'll show it running live. Let's start with why it matters.`,
      `Guardrail: Keep it under 30 seconds. The room came for the demo, not an agenda — don't narrate the outline.`,
    ],
    render: SNTitleSlide,
  },
  {
    id: 2,
    theme: 'beige',
    section: 'The problem · every messaging team asks for the same thing',
    notes: [
      `Key point: Nobody asked for data they don't care about — but Kafka delivers it anyway.`,
      `Script: Every messaging team eventually wants the same thing: only the messages they care about. Here's a topic with 500 distinct keys. This consumer wants three of them. But the Kafka broker is dumb — it ships all 500 to the consumer and leaves the consumer to throw the rest away. Nobody asked for those other 497 keys. You move them anyway, and then you spend more work dropping them.`,
      `Diagram: The full stream of all 500 keys flows from the broker to the consumer; only the 3 requested keys are kept (highlighted), the other 497 are discarded on the consumer side — the broker filters nothing.`,
      `Guardrail: Don't name vendors yet — this is universal to any system without broker-side filtering. Slide 6 names names.`,
    ],
    render: ProblemBeatSlide,
  },
  {
    id: 3,
    theme: 'beige',
    section: 'The cost impact · the dollars you waste are the bytes you discard',
    notes: [
      `Key point: You pay full freight to deliver data straight to the trash.`,
      `Script: Let's put numbers on it. 500 keys, 35,000 messages a second, and this consumer subscribes to three keys. That means 497 of every 500 messages are discarded — 99.4% of the bytes this consumer receives go straight in the bin. And you paid for every one of them twice: once to move them across the network, and again in CPU to deserialize them before you drop them. That waste isn't an edge case — it's the default.`,
      `Diagram: A donut of the consumer's received bytes — a thin sliver (0.6%) is the data it actually wanted; the 99.4% remainder is paid-for transfer that's discarded.`,
      `Guardrail: Don't quote dollar figures here — the cost-receipt slide carries the actual numbers. If asked whether real workloads are this lopsided: yes, often more — fan-out consumers routinely sit above 95% discard.`,
    ],
    render: CostBeatSlide,
  },
  {
    id: 4,
    theme: 'beige',
    section: 'The latency impact · wasted bytes cost wall-clock time',
    notes: [
      `Key point: Those wasted bytes don't just cost money — they cost wall-clock time.`,
      `Script: The same waste shows up as latency. Before your consumer can decide it doesn't want a message, it has to deserialize it — and deserialization is the dominant cost per message. So the consumer burns CPU turning bytes into objects, checks them against its filter, and throws most of them away. You pay that deserialize tax on 497 of every 500 messages, and it lands as delay on the three you actually wanted. Cost and latency, both paid by the consumer, both because the broker is dumb.`,
      `Diagram: An envelope-processing cycle where the bar fills at the deserialize step — that bar is the consumer's CPU working on messages it will immediately discard; the drop happens only after the work is done.`,
      `Guardrail: This is the last problem beat — next we pivot to "this is solvable, and the rest of the world already solved it." If asked why deserialize is the bar: it's the dominant per-message CPU cost; the predicate check and discard are cheap by comparison.`,
    ],
    render: LatencyBeatSlide,
  },
  {
    id: 5,
    theme: 'beige',
    section: 'Prior art · the rest of the messaging world',
    notes: [
      `Key point: Broker-side filtering is a solved problem everywhere except Kafka.`,
      `Script: This isn't a hard problem, and it isn't a new one. JMS shipped message selectors in 2001 — twenty-five years ago. Solace ships topic selectors today. Plenty of messaging systems let the broker decide what a consumer receives. Kafka is the conspicuous exception. So the question isn't "can this be done" — it's "why hasn't Kafka done it."`,
      `Diagram: A prior-art timeline placing JMS selectors (2001) and Solace (today) against Kafka, which has no entry — the ecosystem that's missing the primitive.`,
      `Guardrail: Don't pile on the Kafka community — the framing is "they're alone in lacking it, and we're closing the gap for them." If asked for a source: docs.solace.com, Using-Selectors.htm.`,
    ],
    render: Slide2,
  },
  {
    id: 6,
    theme: 'beige',
    section: 'Prior art in action · the consequence of choosing not to filter at the broker',
    notes: [
      `Key point: Where you put the filter is an architectural choice — and Kafka makes the wrong one by default.`,
      `Script: Same firehose, two outcomes. On the top belt — JMS, Solace — there's a sorter at the broker, so only the matching items ever reach the consumer. On the bottom belt — Kafka — there's no sorter, so everything overflows onto the consumer and it sorts on its own dime. Neither belt is "better Kafka" or "broken Kafka." Same data, same demand, different decision about where the filtering happens.`,
      `Diagram: Two conveyor belts fed by the same firehose — the prior-art belt has a broker sorter passing only matches; the Kafka belt has none and overflows the consumer.`,
      `Guardrail: Let the visual land before narrating. If asked about Kafka Streams / ksqlDB: those are downstream consumers — they receive the firehose then filter; they don't move where the work happens, so the broker is still dumb.`,
    ],
    render: ComparisonSlide,
  },
  {
    id: 7,
    theme: 'beige',
    section: 'KAFKA-6020 · eight years, still labeled “needs KIP”',
    notes: [
      `Key point: The community asked for this eight years ago — and nobody even drafted the proposal.`,
      `Script: This gap has a name: KAFKA-6020. It was opened eight years ago asking for exactly this — server-side filtering. And here's the part that matters: it's still labeled "needs KIP." Not in progress, not rejected — nobody has even written the improvement proposal. The demand was logged, acknowledged, and then it just sat there. That's the opening we set out to fill.`,
      `Diagram: The KAFKA-6020 JIRA timeline with its evidence overlay sliding up — open for eight years, still tagged "needs KIP."`,
      `Guardrail: Pause after "today" and let the stall land, but don't pile on the community — they asked, we built it, everyone wins. If asked: it's at issues.apache.org/jira/browse/KAFKA-6020, a JIRA, not yet a KIP.`,
    ],
    render: Slide3,
  },
  {
    id: 8,
    theme: 'beige',
    section: 'Trajectory · where this is going',
    notes: [
      `Key point: We started with what we could ship, and we're aiming at where the community needs us.`,
      `Script: One acknowledgement before the arc: we're an Apache Pulsar company — Pulsar is our foundation, and it's where this started. We built this selector-filter for a customer, as a Pulsar Function that's in production today, and then we thought: why keep it to ourselves? Why not give it to the streaming community as a whole? That's what these milestones do — each step moves it closer to native Kafka, and the last one is the gift that matters most: contributing it back to Apache Kafka as a KIP. So don't let the Pulsar starting point fool you — it's our origin, not our final destination.`,
      `Diagram: A five-milestone rail, left to right — today's Pulsar-Function filter through to the Apache Kafka KIP; the last two milestones are marked roadmap.`,
      `Guardrail: Milestones 04 and 05 are aspirational — frame them as "the roadmap is open, we want your input," and don't promise dates. If asked when the KIP lands: "as soon as we've had one more round of community input — and Berlin Buzzwords is that input."`,
    ],
    render: TrajectoryOpenerSlide,
  },
  {
    id: 9,
    theme: 'beige',
    section: 'Solution Architecture · Overview',
    notes: [
      `Key point: Three layers, and the three properties they buy you.`,
      `Script: Before we zoom in, here's the whole shape on one slide. Three architecture layers — the SDK on the client, the control plane that registers and orchestrates, and the data plane that does the filtering. And three properties they're built to deliver: performance, resiliency, and scalability. The next few slides are just zoom-ins on this map.`,
      `Diagram: Six stacked tiles — SDK, Control Plane, Data Plane (the layers, in blue) over Performance, Resiliency, Scalability (the properties, in teal).`,
      `Guardrail: Keep it to ~10 seconds — it's a map, not a stop. The depth comes on the slides that follow.`,
    ],
    render: SolutionArchDividerSlide,
  },
  {
    id: 10,
    theme: 'beige',
    section: 'The solution · two key components',
    notes: [
      `Key point: The whole system is two pieces a developer touches — plus a durable spine that makes it reliable.`,
      `Script: At the highest level there are two components. One is the SDK, on the consumer side — that's what you write, and it's one line. The other is the filtering engine, on the broker side — that's what you deploy. Between them sits a durable control plane that wires the two together and remembers what each consumer asked for. That last part is what separates this from a best-effort, in-memory filter: your selector survives both the consumer and the broker restarting.`,
      `Diagram: Two component panels — Native Kafka SDK (client) and Filtering engine (server) — joined by a durable control-plane callout in the middle.`,
      `Guardrail: Spend roughly equal time on both halves. The next two slides zoom into the SDK; the architecture section after that zooms into the engine.`,
    ],
    render: TwoComponentsSlide,
  },
  {
    id: 11,
    theme: 'beige',
    section: 'Native Kafka SDK · Consumer Creation',
    notes: [
      `Key point: Opting in is one line — and what you get back is a real KafkaConsumer.`,
      `Script: Here's the entire opt-in. You build your consumer exactly as you do today, and you add one call: dot-selector, with a JMS-style predicate. That's it. What comes back is a standard KafkaConsumer — same interface in, same interface out. No fork, no patched client, no wire-protocol change. The filtering is now happening on the broker-side, and your code barely changed.`,
      `Diagram: Three code cards — standard KafkaConsumer in on the left, the one-line .selector(...) opt-in highlighted in the middle, standard KafkaConsumer out on the right.`,
      `Guardrail: Hold on the middle card and let the room read the one line. If asked about the selector syntax: it's JMS-style — we adopted it, didn't invent it, so there's no new DSL to learn. If asked about client versions: any client supporting the standard Producer/Consumer API.`,
    ],
    render: ConsumerCreationSlide,
  },
  {
    id: 12,
    theme: 'beige',
    section: 'Native Kafka SDK · Dynamic Filter Changes',
    notes: [
      `Key point: You change what a consumer filters without redeploying it.`,
      `Script: Filters aren't fixed at startup. Call setSelector at runtime and the new predicate propagates to the broker in about a second — no redeploy, no restart, no config push. That's the operational story: changing what you consume is a method call, not a deployment. And it's safe — the SDK doesn't acknowledge the change until the broker confirms it's live, so the consumer never sees a half-applied filter.`,
      `Diagram: An animated round trip — setSelector posts the new selector up the control path; the broker's TableView picks it up and the filter switches, all in roughly a second.`,
      `Guardrail: If asked about persistence across restarts: the durable control plane holds the selector, so it survives both ends restarting. If asked about races or partial state: none is ever visible — the change is confirmed before it's acknowledged.`,
    ],
    render: DynamicFilterChangesSlide,
  },
  {
    id: 13,
    theme: 'beige',
    section: 'Architecture · the master map',
    notes: [
      `Key point: Two planes, one data flow — and everything in this section is a zoom-in on this map.`,
      `Script: This is the densest slide in the deck, so let's slow down. Establish one rule first: the control plane lives on top, the data plane on the bottom. Hold that picture, because the next four slides are all zoom-ins on regions of this map. Now follow one filter's whole life: the SDK registers a selector, the orchestrator spawns a filter function, the function consumes the source topic, deserializes each record once, evaluates the selectors, publishes the matches to an output topic, and the consumer drains that output topic. Eight steps, two planes, one flow.`,
      `Diagram: An animated 8-step lifecycle map — control plane (indigo) on top, data plane (teal) on the bottom — tracing one filter from registration through to the consumer draining its output topic.`,
      `Guardrail: Don't try to read every label — nobody catches them all; land the spatial rule and "two planes, one data flow." Defer the orchestrator's "why" to the next slide. If asked about back-pressure: Pulsar's standard semantics apply at each topic edge; we add no new flow control.`,
    ],
    render: ArchMasterSlide,
  },
  {
    id: 14,
    theme: 'beige',
    section: 'Output topics · resilience by design',
    notes: [
      `Key point: Giving each filter its own output topic is what makes the design resilient.`,
      `Script: Every selector's matches land in their own output topic, and that one decision buys three things. Resilience: if the consumer crashes, the matches are already on disk — it reconnects and resumes, with no reprocessing of the source. Per-filter retention: you tune the TTL to that one consumer instead of forcing the shared source topic to carry everyone's worst-case retention. And resume-from-latest: a consumer that only cares about "now" can seek to the latest offset and skip the backlog entirely.`,
      `Diagram: One source topic fanning into per-consumer output topics on the right, each independently retained and independently resumable.`,
      `Guardrail: If asked about disk cost: yes, per-filter topics aren't free — that's an honest trade we cover on the scale slide. If asked about overlapping selectors: a record matching two selectors is written once into each of the two output topics.`,
    ],
    render: ArchOutputTopicsSlide,
  },
  {
    id: 15,
    theme: 'beige',
    section: 'Control plane · the Kubernetes-controller pattern',
    notes: [
      `Key point: The control plane is a reconcile loop — the same pattern Kubernetes uses.`,
      `Script: If you've operated Kubernetes, you already know this architecture. It's a reconcile loop: the desired state is the set of selectors you've registered, the actual state is the filter functions running, and the orchestrator continuously converges one onto the other. State lives in a TableView with two keyspaces — one mapping each query to its selector, the other mapping each filter to its assignment. A filter's lifecycle has four commands: register a query, modify its selector, unregister it when the consumer is done — and a heartbeat. The heartbeat is the critical one: it's how the system detects a consumer that crashed without unregistering, so the orchestrator can reclaim that filter instead of leaving it running forever. We didn't invent this pattern; we adopted a known-good one, so its failure modes are already familiar.`,
      `Diagram: A reconcile ring marching around the orchestrator, with the TableView's two keyspaces and REGISTER / MODIFY shown as JSON over the wire.`,
      `Guardrail: If asked about leader election or split-brain: it's a standard single-instance-per-partition Pulsar Function; the broker handles failover — defer to "Pulsar's existing semantics."`,
    ],
    render: ArchControlPlaneSlide,
  },
  {
    id: 16,
    theme: 'beige',
    section: 'Filter function · one per source topic, deserialize once',
    notes: [
      `Key point: One filter function per source topic — deserialize once, evaluate every selector in parallel.`,
      `Script: This is the decision that answers "doesn't this just move the work, not remove it?" There's one filter function per source topic, and every selector targeting that source shares it. So a record is deserialized exactly once, then every selector evaluates against that same deserialized record in parallel, and the matches fan out. The cost is paid once per source record and amortized across all of that source's consumers — adding more queries does not add more deserialization, and it doesn't add more functions.`,
      `Diagram: One pipe in, N pipes out — source topic into a single deserialize step into the filter funnel, selectors applied in parallel, fanning to N output topics.`,
      `Guardrail: If asked about selector compilation: selectors compile once at register time, so per-record cost is evaluation, not parsing. If asked about a source heavier than one function can handle: the next slide covers Key_Shared plus autoscaling.`,
    ],
    render: ArchProcessorSlide,
  },
  {
    id: 17,
    theme: 'beige',
    section: 'Scale + honest trade-offs',
    notes: [
      `Key point: It scales without rebalancing — and the only thing you add is managed compute you control, while your consumer gets simpler.`,
      `Script: Two things to land here. First, scale: per-key parallelism comes from Pulsar's Key_Shared subscription, and the filter pods autoscale with Kubernetes HPA — no repartitioning, no consumer-group rebalances. Second, the honest accounting. What doesn't change: your client is the same KafkaConsumer, and your consumer logic actually gets simpler, because the filter code is deleted. What you do trade: the broker is a wire-compatible swap — Pulsar with KoP today, native Kafka on the roadmap — and the filter is managed compute that you size. So the win isn't "nothing runs" — it's "there's no bespoke filtering app for you to build, deploy, and operate."`,
      `Script (as the left panel animates): Watch the cells fill in. As load climbs, the framework adds filter instances on its own — that's Kubernetes HPA scaling out. And because Pulsar's Key_Shared subscription pins each key to a single consumer, every key stays in order the whole time. So the framework auto-scales while retaining per-key ordering — no repartitioning, and no consumer-group rebalance. On the right, the two bands track what stays the same (your client) and what you trade (the broker swap and the managed filter compute).`,
      `Guardrail: Say the win-callout out loud to pre-empt "but you said no overhead." If asked about the cost ratio: the cost slide shows ~256× saved per dollar of compute — the managed compute is dwarfed by the egress it eliminates.`,
    ],
    render: ArchScalingSlide,
  },
  {
    id: 18,
    theme: 'beige',
    section: 'What We\'re About to Demo',
    notes: [
      `Key point: Before the live demo, here's exactly what you're about to watch.`,
      `Script: Quick setup. Before we run anything live, three short slides on what the demo is: the three layers in play, the scenario we're filtering, and how it's all wired together. Then we run it.`,
      `Guardrail: Five seconds — this just frames the next three slides as setup, not the demo itself.`,
    ],
    render: DemoIntroDividerSlide,
  },
  {
    id: 19,
    theme: 'beige',
    section: 'Three layers · two of them are vanilla Kafka',
    notes: [
      `Key point: Two of the three layers are plain, unmodified Kafka — only the broker changes.`,
      `Script: Three layers, top to bottom — and read them with me, because two of the three are completely ordinary Kafka. Start at the top, the producer: that's ShadowTraffic feeding a stock-quotes stream through a standard Kafka producer — plain records with a few headers like exchange, sector, and side, and zero Pulsar awareness, no SDK. Now jump to the bottom, the consumer: a standard KafkaConsumer with an ordinary poll loop, a StringDeserializer, consumer groups — all the Kafka you already know. The only thing different down there is a single .selector() call, and even that hands you back a real KafkaConsumer. So the producer is vanilla, the consumer is vanilla. The one layer that actually changes is the middle one — the broker. It's a Kafka-API-compatible broker, Pulsar with KoP today, and it's where the filter function lives: it deserializes each record once, evaluates every selector, and fans the matches out.`,
      `Diagram: Three stacked bands — producer (vanilla) and consumer (vanilla) bracketing the broker (the one swap), with a guardrail line at the foot.`,
      `Guardrail: Read the guardrail line out loud — "wire-compatible swap, not the same broker" and "managed compute you size, not free" — to pre-empt the two most common objections.`,
    ],
    render: DemoThreeLayersSlide,
  },
  {
    id: 20,
    theme: 'beige',
    section: 'The demo · Stock Watchlist',
    notes: [
      `Key point: One stock stream, nine watchlists, filtered at the broker — with zero client-side filtering.`,
      `Script: Here's the scenario. A single stock-quote stream feeds nine different watchlists, each wanting a different slice of the market, and all nine are filtered at the broker. The numbers that matter: nine consumer slices, one filter function, zero client-side filters and zero Pulsar client libraries on the consumers. On the right is the cast — five components — and the two to watch are the broker's filter function, which holds all nine selectors and deserializes once, and the multi-query app, which is plain Java doing two jobs: registering selectors and consuming matches.`,
      `Diagram: Left, the scenario line plus the 9 / 1 / 0 metric trio; right, the five-component cast, including the observability UI that lets us look inside the filter live.`,
      `Guardrail: Spend the most time on the filter function and the multi-query app — they're the heart of the demo.`,
    ],
    render: DemoScenarioSlide,
  },
  {
    id: 21,
    theme: 'beige',
    section: 'How it\'s wired · end to end',
    notes: [
      `Key point: Producer unchanged, consumer unchanged — the broker plus the filter is the only thing that's different, and the clients never know.`,
      `Script: Here's the whole thing wired end to end. ShadowTraffic generates the stock-quotes stream; it hits the broker, where one filter function holds nine selectors, deserializes each record once, and fans the matches out to nine private output topics; and the multi-query app drains those topics through nine ordinary KafkaConsumers. Registration flows up — the SDK registers the nine selectors — and matches flow down. The producer didn't change, the consumers didn't change. Only the broker did, and neither end can tell.`,
      `Diagram: An animated left-to-right hero — ShadowTraffic → stock-quotes → broker (1 filter, 9 selectors, 9 output topics) → multi-query app — with a control-blue registration arc over the top and a looping AAPL tile through the broker.`,
      `Guardrail: Trace the looping AAPL tile to anchor "one record in, deserialized once, evaluated against all nine, fanned out to matches." The amber box reading into the broker is the live UI you'll demo next.`,
    ],
    render: DemoWiringSlide,
  },
  {
    id: 22,
    theme: 'beige',
    section: 'Broker-Side Filtering Demo · Native Kafka Producer and Consumer',
    notes: [
      `Key point: Now watch it run — native Kafka producer, native Kafka consumer, broker-side filter in the middle.`,
      `Script: Let's see it live. Native Kafka producer on the left, native Kafka consumer on the right, the broker-side filter in between — all running locally on Docker. I'll do three things: register a selector, watch the matches start flowing, then change the selector live and watch the change propagate.`,
      `Diagram: Demo section-break card — "Broker-Side Filtering Demo · Native Kafka Producer and Consumer."`,
      `Guardrail: If the demo won't start, fall back to the three demo-intro slides — they show everything the live run would — and let the receipts carry the proof. Narrate around any failure; don't stall.`,
    ],
    render: DemoDividerSlide,
  },
  {
    id: 23,
    theme: 'beige',
    section: 'Section 06 · Receipts',
    notes: [
      `Key point: We don't just claim this works — here are the receipts.`,
      `Script: That's the system. Now the proof. Two things to show: that the latency cost is small and explainable, and that the dollar savings are real. Let's take them in order.`,
      `Diagram: Split section divider — "Performance / at Scale" / "Latency & Cost" over the latency graphic.`,
      `Guardrail: Keep it to a beat — this just opens the proof section.`,
    ],
    render: Slide20,
  },
  {
    id: 24,
    theme: 'beige',
    section: 'End-to-end latency · the apparent contradiction',
    notes: [
      `Key point: Sub-millisecond filtering and a 125 ms dashboard are both true — they measure different things.`,
      `Script: There's an apparent contradiction to clear up. We said filtering is sub-millisecond. The demo dashboard says 125 milliseconds end-to-end. Both numbers are real. The under-a-millisecond figure is the filter's own CPU work, on a clean broker clock. The 125 is the entire journey — producer timestamp to a durably-persisted filtered copy — and most of that journey happens around the filter, not inside it. They're not in conflict; they're measuring different spans.`,
      `Diagram: Two cards — the claim (< 1 ms, broker-only) and the dashboard (125 ms p50, end-to-end) — bridged by a "both true" badge.`,
      `Guardrail: If asked about the abstract's "p99 < 25 ms": that was a co-located run; the keystone slide closes that loop. Section numbers: eval < 1 ms, output persist ~15 ms, two persists ~25 ms, transport ~100 ms, end-to-end p50 ~125 ms.`,
    ],
    render: Slide21,
  },
  {
    id: 25,
    theme: 'beige',
    section: 'Where the 125 ms goes · the money slide',
    notes: [
      `Key point: Break the 125 ms apart and filtering is the thinnest slice — the rest is transport and durability.`,
      `Script: So let's break the 125 milliseconds apart. The biggest piece, about 100 milliseconds, is inbound transport — a laptop talking to a GCP region over the public internet. That's not the filter, not even the broker; a plain consumer with no filter pays the exact same road trip, and co-located it collapses to about a millisecond. The next piece, about 25 milliseconds, is durability — we persist on the way in and persist the filtered copy on the way out, which is the price any durable pipeline pays not to lose your data. And the filtering itself? Under a millisecond — the thinnest sliver on the chart. The big costs are explainable substrate, not filter cost.`,
      `Diagram: A stacked bar — transport ~100 ms (~80%), durability ~25 ms (~19%), filtering < 1 ms (a near-invisible sliver, called out) — with three prose buckets repeating the split below.`,
      `Guardrail: Walk the bar in visual order — transport, durability, filtering — and hold longest on transport (the "your laptop talking to GCP" reveal). If asked why durability is paid twice: a journal fsync inbound plus the filtered copy outbound — standard for any durable pipeline.`,
    ],
    render: Slide22,
  },
  {
    id: 26,
    theme: 'beige',
    section: 'The measured keystone · local-vs-cloud',
    notes: [
      `Key point: We didn't model the transport cost — we measured it, by running the same demo local and in the cloud.`,
      `Script: And we didn't model that transport number — we ran it. Same demo, same code, same nine queries, two environments. In the cloud, laptop to GCP, two network legs: 217 milliseconds. On one machine, zero network legs: 18. That's a 12× collapse, and the 199-millisecond gap is exactly two network legs at roughly 100 each — proven by subtraction, not estimated. Co-locate like a real production deployment and the 125 becomes about 30. The filtering itself was sub-millisecond the whole time; the rest is transport and durability — the dashboard was just also timing the road trip.`,
      `Diagram: Left, measured cloud (217 ms) vs local (18 ms) bars with the 12× delta; right, a navy payoff card showing 125 ms → ~30 ms co-located.`,
      `Guardrail: If asked why ~30 ms is "inferred": local measured 18 ms transport, plus ~15 ms persist plus < 1 ms eval — components measured, sum inferred. On "why not Flink / KStreams": co-located, latency is comparable — we win on operational simplicity and built-in multi-query orchestration, not on latency.`,
    ],
    render: Slide23,
  },
  {
    id: 27,
    theme: 'beige',
    section: 'The cost receipt · trading compute for network',
    notes: [
      `Key point: A trivial amount of compute eliminates most of your cross-region egress — real, measurable, immediate.`,
      `Script: Here's the dollar side, from a real fintech workload. Frame it as a trade. What you spend: one filter function, two cores and eight gigs of RAM — the compute equivalent of a single AWS m6a.large, about $62.78 a month. What you save: 95% or more of the cross-region traffic, 240 terabytes a month that never leaves the broker, roughly $16,100 a month in data-transfer charges. That's about a 256× return — every dollar of compute saves around $256 of network. The math behind the 240 terabytes: 930 megabits before, 155 after, 755 saved. And it scales linearly with fan-out, so it pays off even at small throughputs. Real, measurable, immediate — bought for the minimal latency cost we just walked through. For most developers, that's an easy trade.`,
      `Diagram: Two cards — spend ($62.78/mo compute) and save (~$16,100/mo egress) — with "≈256× return" between them, a before/after throughput strip, and a "real · measurable · immediate" footer.`,
      `Guardrail: These are the client's own figures, not modeled — cite the ~$16,100 as their measured AWS line item; don't recompute on stage.`,
    ],
    render: CostSlide,
  },
  {
    id: 28,
    theme: 'beige',
    section: 'Roadmap · what would you build?',
    notes: [
      `Key point: We shipped the foundation — now tell us what to build next.`,
      `Script: We shipped the foundation; here's where it goes next. Content-based routing — predicates over the message body, not just headers — and the nice part is the SDK reuses your already-configured deserializers and Schema Registry, so there's nothing new to wire up. Time-window selectors, so a query only fires inside, say, market hours. And remote enable/disable, to pause or resume a query without a redeploy. But the real ask is the last tile: bring us your workloads and tell us which dimension to ship next.`,
      `Diagram: Four cards — content-based routing, time-window selectors, remote enable/disable, and "Your turn" with a "Let's Talk!" call to action.`,
      `Guardrail: This is the audience-engagement beat — Q&A often opens here. If you need to compress, lead with content-based routing (most requested) and remote enable/disable (most operationally useful).`,
    ],
    render: Slide28,
  },
  {
    id: 29,
    theme: 'beige',
    section: 'Open source · try it yourself',
    notes: [
      `Key point: It's open source — scan and run it locally right now.`,
      `Script: This is all open source. The QR code goes to the GitHub repo, and the README opens with a single docker-compose command that brings the whole thing up locally. Scan it, clone it, run the demo you just watched.`,
      `Diagram: Centered QR to the GitHub repo over the dotted-navy template, with the full URL beneath.`,
      `Guardrail: Hold the slide at least five seconds so cameras can catch the QR, and mention the local docker-compose command in the same breath.`,
    ],
    render: Slide29,
  },
  {
    id: 30,
    theme: 'beige',
    section: 'Close · Q&A',
    notes: [
      `Key point: Let's make broker-side filtering part of Apache Kafka — together.`,
      `Script: That's broker-side filtering for Kafka: the primitive the community asked for, built, proven, and open source. Let's make it part of Apache Kafka — together. Thank you — and let's talk. Here's where to find us.`,
      `Diagram: Closing card — "Thank you for your time," both speaker emails, and streamnative.io.`,
      `Guardrail: Have two-sentence answers ready. Stream-processor comparison → the keystone slide; full-payload audit → the broker keeps the source record, tiered storage handles volume; p99 vs selector complexity → selectors compile once at register time. Defer "when is full Kafka coming" to the roadmap, no dates.`,
    ],
    render: ClosingSlide,
  },
] as const;
